export const DURATION_FRAMES = 191;

import React from "react";
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";
import { dark, fonts, safeZone, gradients } from "../styles/theme";

/*
 * Reel_cmohoeglc00003qo979c8hurh
 * [TEST] Reel de prueba
 * 191 frames @ 30fps
 * Scene timings:
 *   Scene 1 "Test": from=0, duration=191
 */

const FloatingParticles: React.FC = () => {
  const frame = useCurrentFrame();
  const particles = Array.from({ length: 16 }, (_, i) => {
    const baseX = (i * 73.3) % 1080;
    const baseY = (i * 137.7) % 1920;
    const dx = noise2D("particle-x-" + i, frame * 0.008, 0) * 80;
    const dy = noise2D("particle-y-" + i, frame * 0.008, 0) * 80;
    const hue = 200 + noise2D("particle-h-" + i, frame * 0.005, 0) * 30;
    const size = 3 + ((i * 7) % 4);
    const opacity = 0.06 + ((i * 11) % 4) * 0.01;
    return (
      <div
        key={i}
        style={{
          position: "absolute",
          left: baseX + dx,
          top: baseY + dy,
          width: size,
          height: size,
          borderRadius: "50%",
          background: `hsl(${hue}, 80%, 65%)`,
          opacity,
          filter: "blur(1px)",
          boxShadow: `0 0 12px hsl(${hue}, 80%, 65%)`,
        }}
      />
    );
  });
  return <>{particles}</>;
};

const LightSweep: React.FC = () => {
  const frame = useCurrentFrame();
  const left = interpolate(frame, [0, 191], [-300, 1380], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left,
        width: 220,
        height: 1920,
        background: "linear-gradient(90deg, transparent, rgba(96,165,250,0.10), transparent)",
        transform: "skewX(-12deg)",
        pointerEvents: "none",
      }}
    />
  );
};

const VignetteOverlay: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 90, 191], [0.45, 0.5, 0.55], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,${opacity}) 100%)`,
        pointerEvents: "none",
      }}
    />
  );
};

const BlueGlow: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 30, 160, 191], [0, 0.9, 0.9, 0.7], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: gradients.darkGlow,
        opacity,
        pointerEvents: "none",
      }}
    />
  );
};

const ContinuousBackground: React.FC = () => {
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <BlueGlow />
      <FloatingParticles />
      <LightSweep />
      <VignetteOverlay />
    </AbsoluteFill>
  );
};

const SceneTest: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 80, mass: 1 },
  });
  const titleOpacity = interpolate(titleSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const titleY = interpolate(titleSpring, [0, 1], [60, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const subSpring = spring({
    frame: frame - 22,
    fps,
    config: { damping: 20, stiffness: 80, mass: 1 },
  });
  const subOpacity = interpolate(subSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const subY = interpolate(subSpring, [0, 1], [60, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const logoSpring = spring({
    frame: frame - 50,
    fps,
    config: { damping: 18, stiffness: 90, mass: 1 },
  });
  const logoOpacity = interpolate(logoSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const logoScale = interpolate(logoSpring, [0, 1], [0.85, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardPulse =
    1 + Math.sin(frame * 0.06) * 0.01;

  const exitOpacity = interpolate(frame, [170, 191], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        opacity: exitOpacity,
        paddingTop: safeZone.top,
        paddingBottom: safeZone.bottom,
        paddingLeft: safeZone.horizontal,
        paddingRight: safeZone.horizontal,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 48,
      }}
    >
      <div
        style={{
          opacity: logoOpacity,
          transform: `scale(${logoScale})`,
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 24px",
          background: dark.surface,
          borderRadius: 999,
          border: `1px solid ${dark.border}`,
          boxShadow: dark.glowBlue,
        }}
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <defs>
            <linearGradient id="logoGrad" x1="0" y1="0" x2="32" y2="32">
              <stop offset="0%" stopColor="#60a5fa" />
              <stop offset="100%" stopColor="#2563eb" />
            </linearGradient>
          </defs>
          <circle cx="16" cy="16" r="14" stroke="url(#logoGrad)" strokeWidth="2.5" fill="none" />
          <circle cx="16" cy="16" r="6" fill="url(#logoGrad)" />
        </svg>
        <span
          style={{
            fontFamily: fonts.headline,
            fontSize: 24,
            fontWeight: 700,
            color: dark.text,
            letterSpacing: 0.5,
          }}
        >
          Core Digital
        </span>
      </div>

      <div
        style={{
          opacity: titleOpacity,
          transform: `translateY(${titleY}px) scale(${cardPulse})`,
          padding: "56px 64px",
          background: "rgba(255,255,255,0.05)",
          borderRadius: 28,
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: dark.glowBlueStrong,
          backdropFilter: "blur(20px)",
          maxWidth: 900,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: fonts.body,
            fontSize: 32,
            fontWeight: 600,
            color: dark.textSecondary,
            textTransform: "uppercase",
            letterSpacing: 4,
            marginBottom: 24,
          }}
        >
          Pipeline Test
        </div>
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 88,
            fontWeight: 800,
            lineHeight: 1.05,
            background: "linear-gradient(135deg, #60a5fa, #2563eb, #818cf8)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Esto es un test
        </div>
      </div>

      <div
        style={{
          opacity: subOpacity,
          transform: `translateY(${subY}px)`,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: dark.accentBlueMuted,
            boxShadow: `0 0 12px ${dark.accentBlueMuted}`,
          }}
        />
        <span
          style={{
            fontFamily: fonts.body,
            fontSize: 32,
            fontWeight: 600,
            color: dark.textSecondary,
            letterSpacing: 1,
          }}
        >
          Generación automática activa
        </span>
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: dark.accentBlueMuted,
            boxShadow: `0 0 12px ${dark.accentBlueMuted}`,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

const Reel_cmohoeglc00003qo979c8hurh: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: dark.bg }}>
      <ContinuousBackground />
      <Sequence from={5}>
        <Audio src={staticFile("audio/cmohoeglc00003qo979c8hurh.mp3")} />
      </Sequence>
      <Sequence from={0} durationInFrames={191}>
        <SceneTest />
      </Sequence>
    </AbsoluteFill>
  );
};

export default Reel_cmohoeglc00003qo979c8hurh;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
